import React, { memo, useEffect, useState, useCallback } from "react";
import {
  InputLabel,
  makeStyles,
  TextField,
  Switch,
  FormControlLabel,
  Box,
  Typography,
  Tooltip,
  Grid,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import EditIcon from "@material-ui/icons/Edit";
import MapIcon from "@material-ui/icons/Map";
import LocationOnIcon from "@material-ui/icons/LocationOn";

// Import Google Maps components with static imports
import { Map, Marker, GoogleApiWrapper } from "google-maps-react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import Geocode from "react-geocode";

// Configure Geocode
Geocode.setApiKey("AIzaSyD_7TfgeItRTC_p6qmO7yJp5evvFCTh9zg");
Geocode.enableDebug();

const GOOGLE_API_KEY = "AIzaSyD_7TfgeItRTC_p6qmO7yJp5evvFCTh9zg";

/**
 * AddressInput Component
 * 
 * A hybrid address input component that supports:
 * 1. Google Maps autocomplete (when API is available and billing is active)
 * 2. Manual text input (fallback when Google API fails or user prefers manual entry)
 * 
 * Props:
 * - label: Label for the input field
 * - data: Object with { label, lat, lng } - current address data
 * - onChange: Callback when address changes
 * - onBlur: Callback when input loses focus
 * - appView: If true, component is in read-only view mode
 * - fullWidth: If true, map takes full width
 * - forceManualMode: If true, always use manual input mode
 */
const AddressInput = ({
  appView = false,
  fullWidth = false,
  label,
  data = {},
  google,
  onChange,
  onBlur,
  forceManualMode = false,
}) => {
  const classes = useStyles();
  
  // State
  const [isManualMode, setIsManualMode] = useState(forceManualMode);
  const [googleApiError, setGoogleApiError] = useState(false);
  const [address, setAddress] = useState(data?.label || "");
  const [manualLat, setManualLat] = useState(data?.lat || "");
  const [manualLng, setManualLng] = useState(data?.lng || "");
  const [mapCenter, setMapCenter] = useState({
    lat: data?.lat || 40.4093,
    lng: data?.lng || 49.8671, // Default to Baku, Azerbaijan
  });
  const [optionList, setOptionList] = useState([]);
  const [showOption, setShowOption] = useState(false);
  const [showHeightDragend, setShowHeightDragend] = useState(false);
  const [loading, setLoading] = useState(false);

  // Update local state when data prop changes
  useEffect(() => {
    if (data?.label !== address) {
      setAddress(data?.label || "");
    }
    if (data?.lat && data?.lng) {
      setMapCenter({ lat: data.lat, lng: data.lng });
      setManualLat(data.lat);
      setManualLng(data.lng);
    }
  }, [data?.label, data?.lat, data?.lng]);

  // Check Google API availability on mount
  useEffect(() => {
    if (!forceManualMode) {
      // Test if Google Maps API is working
      const testGoogleApi = async () => {
        try {
          // Simple check - if google object exists and places service is available
          if (!google || !google.maps || !google.maps.places) {
            console.warn("[AddressInput] Google Maps API not fully loaded");
            setGoogleApiError(true);
            setIsManualMode(true);
          }
        } catch (error) {
          console.error("[AddressInput] Google API test failed:", error);
          setGoogleApiError(true);
          setIsManualMode(true);
        }
      };
      
      // Delay check to allow Google API to initialize
      const timer = setTimeout(testGoogleApi, 2000);
      return () => clearTimeout(timer);
    }
  }, [google, forceManualMode]);

  // Handle Google autocomplete change
  const handleGoogleChange = useCallback((newAddress) => {
    setAddress(newAddress);
    if (newAddress === "") {
      setOptionList([]);
      onChange({ label: "", lat: null, lng: null });
      setShowOption(false);
      setShowHeightDragend(false);
    }
  }, [onChange]);

  // Handle Google autocomplete select
  const handleGoogleSelect = useCallback(async (selectedAddress) => {
    try {
      setLoading(true);
      const results = await geocodeByAddress(selectedAddress);
      const latLng = await getLatLng(results[0]);
      
      const newData = {
        label: selectedAddress,
        lat: latLng.lat,
        lng: latLng.lng,
      };
      
      onChange(newData);
      setMapCenter(latLng);
      setAddress(selectedAddress);
      setManualLat(latLng.lat);
      setManualLng(latLng.lng);
      setShowOption(true);
      setShowHeightDragend(false);
      setLoading(false);
    } catch (error) {
      console.error("[AddressInput] Geocode error:", error);
      setLoading(false);
      
      // If geocoding fails, switch to manual mode
      if (error?.message?.includes("OVER_QUERY_LIMIT") || 
          error?.message?.includes("REQUEST_DENIED") ||
          error?.message?.includes("billing")) {
        setGoogleApiError(true);
        setIsManualMode(true);
      }
    }
  }, [onChange]);

  // Handle marker drag end
  const handleMarkerDragEnd = useCallback((event, coord) => {
    if (!Geocode) return;
    
    const newLat = coord.internalPosition.lat();
    const newLng = coord.internalPosition.lng();

    if (newLat !== undefined && newLng !== undefined) {
      setMapCenter({ lat: newLat, lng: newLng });
      setManualLat(newLat);
      setManualLng(newLng);
    }

    Geocode.fromLatLng(newLat, newLng).then(
      (response) => {
        const options = response.results.filter(
          (item) =>
            !item.types.includes("political") &&
            !item.types.includes("plus_code")
        );

        const newArr = options.map((item) => ({
          active: false,
          description: item.formatted_address,
        }));

        setAddress("");
        setOptionList(newArr);
        setShowOption(false);
        setShowHeightDragend(true);
      },
      (error) => {
        console.error("[AddressInput] Reverse geocode error:", error);
        // Still update coordinates even if reverse geocoding fails
        onChange({
          label: address || `${newLat.toFixed(6)}, ${newLng.toFixed(6)}`,
          lat: newLat,
          lng: newLng,
        });
      }
    );
  }, [address, onChange]);

  // Handle manual address input
  const handleManualAddressChange = useCallback((e) => {
    const newAddress = e.target.value;
    setAddress(newAddress);
    onChange({
      label: newAddress,
      lat: manualLat || null,
      lng: manualLng || null,
    });
  }, [onChange, manualLat, manualLng]);

  // Handle manual coordinate input
  const handleManualCoordinateChange = useCallback((field, value) => {
    const numValue = value === "" ? "" : parseFloat(value);
    
    if (field === "lat") {
      setManualLat(numValue);
      onChange({
        label: address,
        lat: numValue || null,
        lng: manualLng || null,
      });
    } else {
      setManualLng(numValue);
      onChange({
        label: address,
        lat: manualLat || null,
        lng: numValue || null,
      });
    }
  }, [onChange, address, manualLat, manualLng]);

  // Toggle between manual and Google mode
  const handleModeToggle = useCallback(() => {
    if (googleApiError && !isManualMode) {
      // Can't switch to Google mode if API has errors
      return;
    }
    setIsManualMode(!isManualMode);
  }, [isManualMode, googleApiError]);

  // Render Google autocomplete mode
  const renderGoogleMode = () => {
    return (
      <PlacesAutocomplete
        value={address || ""}
        onChange={handleGoogleChange}
        onSelect={handleGoogleSelect}
        onError={(status, clearSuggestions) => {
          console.error("[AddressInput] Places API error:", status);
          if (status === "OVER_QUERY_LIMIT" || status === "REQUEST_DENIED") {
            setGoogleApiError(true);
            setIsManualMode(true);
          }
          clearSuggestions();
        }}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading: suggestionsLoading }) => {
          if (suggestions && suggestions.length > 0) {
            setOptionList(suggestions);
          }

          return (
            <>
              <TextField
                {...getInputProps({
                  className: "location-search-input",
                })}
                onBlur={onBlur}
                value={address}
                type="text"
                fullWidth
                disabled={appView}
                size="small"
                variant="outlined"
                className={classes.inputStyle}
                placeholder="Ünvanı daxil edin..."
              />

              <div className="autocomplete-dropdown-container">
                {suggestionsLoading && <div className={classes.loadingText}>Yüklənir...</div>}

                {!showOption && optionList.map((suggestion, index) => {
                  const className = suggestion.active
                    ? "suggestion-item--active"
                    : "suggestion-item";
                  const style = suggestion.active
                    ? { backgroundColor: "#fafafa", cursor: "pointer", padding: "8px 12px" }
                    : { backgroundColor: "#ffffff", cursor: "pointer", padding: "8px 12px" };
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion, { className, style })}
                      key={index}
                    >
                      <LocationOnIcon style={{ fontSize: 16, marginRight: 8, color: "#666" }} />
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </div>
            </>
          );
        }}
      </PlacesAutocomplete>
    );
  };

  // Render manual input mode
  const renderManualMode = () => (
    <Box>
      <TextField
        value={address}
        onChange={handleManualAddressChange}
        onBlur={onBlur}
        type="text"
        fullWidth
        disabled={appView}
        size="small"
        variant="outlined"
        className={classes.inputStyle}
        placeholder="Ünvanı əl ilə daxil edin..."
      />
      
      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Grid item xs={6}>
          <TextField
            label="Enlik (Latitude)"
            value={manualLat}
            onChange={(e) => handleManualCoordinateChange("lat", e.target.value)}
            type="number"
            fullWidth
            disabled={appView}
            size="small"
            variant="outlined"
            className={classes.inputStyle}
            placeholder="40.4093"
            inputProps={{ step: "0.000001" }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Uzunluq (Longitude)"
            value={manualLng}
            onChange={(e) => handleManualCoordinateChange("lng", e.target.value)}
            type="number"
            fullWidth
            disabled={appView}
            size="small"
            variant="outlined"
            className={classes.inputStyle}
            placeholder="49.8671"
            inputProps={{ step: "0.000001" }}
          />
        </Grid>
      </Grid>
      
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: "block" }}>
        💡 Koordinatları Google Maps-dən kopyalaya bilərsiniz: maps.google.com
      </Typography>
    </Box>
  );

  // Render map (if coordinates are available)
  const renderMap = () => {
    if (!Map || !google || !data?.lat || !data?.lng) return null;

    return (
      <Map
        google={google}
        initialCenter={{ lat: mapCenter.lat, lng: mapCenter.lng }}
        center={{ lat: mapCenter.lat, lng: mapCenter.lng }}
        style={{ width: "100%", height: "300px", marginTop: "10px" }}
        containerStyle={{
          display: "block",
          width: fullWidth || appView ? "100%" : "100%",
          height: "300px",
          position: "relative",
          left: 0,
        }}
      >
        <Marker
          draggable={!appView && !isManualMode}
          onDragend={handleMarkerDragEnd}
          position={{ lat: mapCenter.lat, lng: mapCenter.lng }}
        />
      </Map>
    );
  };

  return (
    <div className={classes.container}>
      {/* Header with label and mode toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
        <InputLabel className={classes.label}>{label}</InputLabel>
        
        {!appView && !forceManualMode && (
          <Tooltip title={isManualMode ? "Google Maps-ə keç" : "Əl ilə daxiletməyə keç"}>
            <FormControlLabel
              control={
                <Switch
                  checked={isManualMode}
                  onChange={handleModeToggle}
                  color="primary"
                  size="small"
                  disabled={googleApiError}
                />
              }
              label={
                <Typography variant="caption" style={{ display: "flex", alignItems: "center" }}>
                  {isManualMode ? (
                    <span style={{ display: "flex", alignItems: "center" }}>
                      <EditIcon style={{ fontSize: 14, marginRight: 4 }} />
                      Əl ilə
                    </span>
                  ) : (
                    <span style={{ display: "flex", alignItems: "center" }}>
                      <MapIcon style={{ fontSize: 14, marginRight: 4 }} />
                      Google
                    </span>
                  )}
                </Typography>
              }
              labelPlacement="start"
              style={{ marginRight: 0 }}
            />
          </Tooltip>
        )}
      </Box>

      {/* API Error Alert */}
      {googleApiError && (
        <Alert severity="warning" className={classes.alert}>
          Google Maps xidməti hazırda əlçatan deyil. Zəhmət olmasa ünvanı əl ilə daxil edin.
        </Alert>
      )}

      {/* Input Section */}
      {isManualMode ? renderManualMode() : renderGoogleMode()}

      {/* Map Section */}
      {data?.lat && data?.lng && (
        <Box className={classes.mapContainer}>
          {renderMap()}
        </Box>
      )}

      {/* Loading overlay */}
      {loading && (
        <Box className={classes.loadingOverlay}>
          <Typography>Yüklənir...</Typography>
        </Box>
      )}
    </div>
  );
};

// Memoized wrapper
const MemoizedAddressInput = memo(
  (props) => (
    <div className="address-input-wrapper">
      <AddressInput {...props} />
    </div>
  ),
  (prevProps, nextProps) => {
    // Only re-render if data.label changes
    return prevProps.data?.label === nextProps.data?.label &&
           prevProps.data?.lat === nextProps.data?.lat &&
           prevProps.data?.lng === nextProps.data?.lng;
  }
);

// Styles
const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    position: "relative",
  },
  label: {
    marginBottom: 4,
    fontWeight: 500,
  },
  inputStyle: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: "1px solid #d1d5db",
      },
      "& input": {
        boxSizing: "border-box",
        padding: "0px 10px",
        height: "52px",
        color: "#000",
      },
    },
  },
  alert: {
    marginBottom: 12,
    fontSize: "0.85rem",
  },
  mapContainer: {
    marginTop: 16,
    height: 300,
    position: "relative",
  },
  loadingText: {
    padding: "8px 12px",
    color: "#666",
    fontStyle: "italic",
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
}));

// Export with Google API wrapper
export default GoogleApiWrapper({
  apiKey: GOOGLE_API_KEY,
})(MemoizedAddressInput);

// Also export the manual-only version for cases where Google API is definitely not needed
export const ManualAddressInput = (props) => <MemoizedAddressInput {...props} forceManualMode={true} />;
